import React, {
  Dispatch,
  SetStateAction,
  MutableRefObject,
  ReactElement,
  useEffect,
} from 'react';
import YouTube from 'react-youtube';
import type YouTubePlayer from 'youtube-player';
import GameEngine from 'react-game-engine';
import { useNavigate, useLocation } from 'react-router-dom';

import events from '../events';
import { getWindowDims, getVideoId, getCurrentPath } from '../../../helpers';
import uris from '../../../uris';
import PATHS from '../../../constants/paths';
import { GAME_MODES } from '../../../constants/defaults';
import { SONGS } from '../../../examples';
import { Song } from '../../../models';

interface VideoBackgroundProps {
  song: Song;
  setOpacity: Dispatch<SetStateAction<number>>;
  videoPlayer: YouTubePlayer;
  setVideoPlayer: Dispatch<SetStateAction<YouTubePlayer>>;
  setPlayerState: Dispatch<SetStateAction<number>>;
  gameEngineRef: MutableRefObject<GameEngine>;
}

function VideoBackground({
  song,
  setOpacity,
  videoPlayer,
  setVideoPlayer,
  setPlayerState,
  gameEngineRef,
}: VideoBackgroundProps): ReactElement {
  const windowDims = getWindowDims();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { gameMode } = state ?? {};
  const initVideoId = getVideoId(SONGS[0].source);

  // Video management
  const currentPath = getCurrentPath();
  const resetVideo = (player) => {
    player?.pauseVideo();
    player?.seekTo(0, true);
    setOpacity(1);
  };

  useEffect(() => {
    const playerState = videoPlayer?.getPlayerState(); // Can't use `playerState` since may not be updated
    const willPlay =
      (!playerState && song.source) ||
      playerState === YouTube.PlayerState.PLAYING ||
      playerState === YouTube.PlayerState.BUFFERING ||
      playerState === YouTube.PlayerState.UNSTARTED;

    if (currentPath !== PATHS.arcade) resetVideo(videoPlayer);
    else if (currentPath === PATHS.arcade && (!gameMode || !willPlay)) {
      if (gameMode === GAME_MODES.record || gameMode === GAME_MODES.review)
        navigate(uris.studio);
      else navigate(uris.menu);
    }
  }, [currentPath]);

  // Control callbacks
  const onReady = (event) => setVideoPlayer(event.target);
  const onPlay = (event) => {
    if (currentPath !== PATHS.arcade) resetVideo(event.target);
    else setOpacity(0);
  };
  const onEnd = () => {
    const gameState = gameEngineRef.current?.state.entities.state;

    if (currentPath === PATHS.arcade) {
      if (gameMode === GAME_MODES.play)
        navigate(uris.ending, { state: gameState });
      else if (gameMode === GAME_MODES.record)
        navigate(uris.production, { state: gameState });
      else if (gameMode === GAME_MODES.review) navigate(uris.studio);
      else navigate(uris.menu);
    }
  };
  const onStateChange = (event) => {
    gameEngineRef.current?.dispatch(events.yTPSC(event.data));
    setPlayerState(event.data);
  };
  const onError = () => setPlayerState(YouTube.PlayerState.UNSTARTED - 1);

  // Styling
  const opts = {
    width: String(windowDims.w),
    height: String(windowDims.h),
    frameBorder: 0,
    playerVars: {
      controls: 0 as const,
      disablekb: 1 as const,
      modestbranding: 1 as const,
      playsinline: 1 as const,
      iv_load_policy: 3 as const,
    },
  };
  const videoBackgroundStyle = {
    position: 'absolute' as const,
    top: 0,
    left: '50%',
    marginLeft: -windowDims.w / 2,
    width: windowDims.w,
    height: windowDims.h,
    overflow: 'hidden', // Needed to remove white strip at bottom
    pointerEvents: 'none' as const,
  };

  return (
    <div style={videoBackgroundStyle}>
      <YouTube
        videoId={initVideoId}
        title="Video Background"
        opts={opts}
        onReady={onReady}
        onPlay={onPlay}
        onEnd={onEnd}
        onStateChange={onStateChange}
        onError={onError}
      />
    </div>
  );
}

export default VideoBackground;
